import type {
  LedgerAccountsListResponse,
  LedgerAccountsQueryParams,
} from './types';

import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type VestigasHTTPError } from '@vestigas/data';

import { queryKeysLedgerAccount } from '../queryKeys';

import { fetchLedgerAccounts } from './fetchLedgerAccounts';
import { zodLedgerAccountsQueryParamsSchema } from './validations';

/**
 * Generates the query options for the ledger-accounts query so they can be
 * shared between the useQuery hook and route-loader prefetching.
 *
 * Query params are parsed through the request schema before they become the
 * cache key, so URL-only fields such as `page` cannot fork a second entry
 * from the same API request.
 *
 * @param queryParams - The query parameters for fetching ledger accounts.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getLedgerAccountsQueryOptions = <
  TData = LedgerAccountsListResponse,
>({
  queryParams,
  options,
}: {
  queryParams: Partial<LedgerAccountsQueryParams>;
  options?: Omit<
    UseQueryOptions<LedgerAccountsListResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const canonicalQueryParams =
    zodLedgerAccountsQueryParamsSchema.parse(queryParams);

  return queryOptions({
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchLedgerAccounts(canonicalQueryParams, { signal }),
    queryKey: queryKeysLedgerAccount.getAll(canonicalQueryParams),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });
};

/**
 * React Query based custom hook for getting the ledger accounts (Kontenplan)
 * that match the given query parameters. Keeps the previous page's data
 * visible while the next page loads.
 *
 * @param queryParams - The query parameters for fetching ledger accounts.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts/operation/list_ledger_accounts_ledger_accounts_get
 */
export const useQueryLedgerAccounts = <TData = LedgerAccountsListResponse>(
  queryParams: Partial<LedgerAccountsQueryParams>,
  options?: Omit<
    UseQueryOptions<LedgerAccountsListResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getLedgerAccountsQueryOptions<TData>({
      options,
      queryParams,
    }),
  );
